import { Box, Grid, ToggleButton, ToggleButtonGroup } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EndorsementsApi from '../../../api/EndorsementsApi';
import { handleUnauthorizedResponse } from '../../../api/http';
import { AuthData } from '../../../context/auth';
import { ErrorData } from '../../../context/ErrorProvider';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { EndorsementResponse } from '../../../types/endorsements';
import {
	StyledSectionContainer,
	StyledTitle,
	StyledTitleContainer,
} from '../../common';
import EndorsementsTable from '../../EndorsementsTable';
import LargeCenterSpinner from '../../LargeCenterSpinner';
interface Props {
	authData: AuthData;
	policyData: PolicyData;
	errorData: ErrorData;
}

/**
 * The list of endorsements on a policy
 */
const EndorsementsList = (props: Props) => {
	const { t } = useTranslation();
	const { authData, policyData, errorData } = props;

	const { policy } = policyData;

	const [endorsements, setEndorsements] = useState<EndorsementResponse[]>([]);
	const [loading, setLoading] = useState(true);
	const [alignment, setAlignment] = useState('Active');
	const [filteredEndorsements, setFilteredEndorsements] = useState(
		endorsements.filter(
			(e) => e.state !== 'invalidated' && e.state !== 'discarded',
		),
	);

	const handleChange = (
		event: React.MouseEvent<HTMLElement>,
		newAlignment: string,
	) => {
		if (newAlignment) {
			setAlignment(newAlignment);
			if (alignment == 'All') {
				setFilteredEndorsements(
					endorsements.filter(
						(e) => e.state !== 'invalidated' && e.state !== 'discarded',
					),
				);
			} else {
				setFilteredEndorsements(
					endorsements.filter((e) => e.state !== 'discarded'),
				);
			}
		}
	};

	useEffect(() => {
		const source = axios.CancelToken.source();
		async function load() {
			try {
				const response = await EndorsementsApi.getEndorsementsForPolicy(
					authData.auth.authorizationToken,
					policy.locator,
					{ cancelToken: source.token },
				);
				if (response) {
					setEndorsements(response.data);
					setFilteredEndorsements(
						response.data.filter(
							(e) => e.state !== 'invalidated' && e.state !== 'discarded',
						),
					);
				}
			} catch (error) {
				if (axios.isCancel(error)) {
					return;
				} else {
					errorData.handleErrorResponse(
						error,
						`Failed to fetch ${t('endorsement.plural.lower')}`,
					);
					if (await handleUnauthorizedResponse(error, authData)) {
						return;
					}
				}
			}
			setLoading(false);
		}
		load();

		return () => {
			source.cancel();
		};
	}, []);

	if (loading) {
		return <LargeCenterSpinner />;
	}

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={8}>
					<StyledTitleContainer>
						<StyledTitle>{t('endorsement.plural.upper')}</StyledTitle>
					</StyledTitleContainer>
				</Grid>
				<Grid item xs={4}>
					<Box display='flex' justifyContent='flex-end'>
						<ToggleButtonGroup
							color='primary'
							value={alignment}
							exclusive
							onChange={handleChange}
						>
							<ToggleButton
								value='Active'
								size='small'
								style={{
									color: 'white',
									backgroundColor:
										alignment == 'Active' ? '#314974' : '#6B778C',
								}}
							>
								Active
							</ToggleButton>
							<ToggleButton
								value='All'
								size='small'
								style={{
									color: 'white',
									backgroundColor: alignment == 'All' ? '#314974' : '#6B778C',
								}}
							>
								All
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>
				</Grid>
			</Grid>

			<hr />
			<StyledSectionContainer>
				<EndorsementsTable endorsements={filteredEndorsements} />
			</StyledSectionContainer>
		</>
	);
};

export default withPolicyContext<Props>(EndorsementsList);
